import { JsonReader } from './jsonReader'
import type { JsonObject } from './jsonReader'

// latest.json beside each desktop installer on the server; the app's release step writes it, never a person.
export interface ReleaseManifest {
  readonly version: string
  readonly fileName: string
  readonly sizeBytes: number
  readonly sha256: string
  readonly releasedAt: string
}

const VERSION_PATTERN: RegExp = /^\d+\.\d+\.\d+$/
const INSTALLER_FILE_PATTERN: RegExp = /^[A-Za-z0-9._-]+\.exe$/
const SHA256_PATTERN: RegExp = /^[0-9a-f]{64}$/
const ISO_DATE_PATTERN: RegExp = /^\d{4}-\d{2}-\d{2}$/

function readMatching(reader: JsonReader, value: unknown, path: string, pattern: RegExp, expected: string): string {
  const text: string = reader.string(value, path)
  if (!pattern.test(text)) reader.fail(path, `must be ${expected}, got ${JSON.stringify(text)}`)
  return text
}

export function parseReleaseManifest(value: unknown, source: string): ReleaseManifest {
  const reader: JsonReader = new JsonReader(`release manifest ${source}`)
  const raw: JsonObject = reader.object(value, '(root)')
  const version: string = readMatching(reader, raw.version, 'version', VERSION_PATTERN, 'a major.minor.patch version without a build suffix')
  const fileName: string = readMatching(reader, raw.fileName, 'fileName', INSTALLER_FILE_PATTERN, 'a plain .exe file name without a folder')
  if (!fileName.includes(`v${version}`)) reader.fail('fileName', `must carry the manifest version v${version}, got ${JSON.stringify(fileName)}`)
  return {
    version,
    fileName,
    sizeBytes: reader.positiveInteger(raw.sizeBytes, 'sizeBytes'),
    sha256: readMatching(reader, raw.sha256, 'sha256', SHA256_PATTERN, '64 lowercase hex digits'),
    releasedAt: readMatching(reader, raw.releasedAt, 'releasedAt', ISO_DATE_PATTERN, 'a yyyy-MM-dd date'),
  }
}

async function requestManifest(url: string, signal: AbortSignal): Promise<Response> {
  try {
    return await fetch(url, { cache: 'no-cache', signal })
  } catch (error) {
    if (signal.aborted) throw error
    throw new Error(`Fetching release manifest ${url} failed: ${(error as Error).message}`, { cause: error })
  }
}

async function readJsonBody(response: Response, url: string): Promise<unknown> {
  try {
    return await response.json()
  } catch (error) {
    throw new Error(`Reading release manifest ${url} failed: the response is not JSON (content-type ${response.headers.get('content-type') ?? 'missing'}): ${(error as Error).message}`, { cause: error })
  }
}

/** Fetches and validates the manifest; always revalidates so a new release shows at once. */
export async function fetchReleaseManifest(url: string, signal: AbortSignal): Promise<ReleaseManifest> {
  const response: Response = await requestManifest(url, signal)
  if (!response.ok) throw new Error(`Fetching release manifest ${url} failed: HTTP ${response.status} ${response.statusText}`)
  return parseReleaseManifest(await readJsonBody(response, url), url)
}
